import type { ReactNode } from 'react'
import { Badge, Collapsible, Link, Text, cn } from '@cloudflare/kumo'
import {
  CaretDownIcon,
  EnvelopeSimpleIcon,
  FacebookLogoIcon,
  InstagramLogoIcon,
  LinkedinLogoIcon,
  MapPinIcon,
  NavigationArrowIcon,
  PhoneIcon,
  WhatsappLogoIcon,
  YoutubeLogoIcon,
} from '@phosphor-icons/react'
import type { AmenityCategory, Building, BuildingAmenity } from '@hub-wifi/shared'
import { useTranslation } from '../../i18n'
import { hoursForToday } from './building-status'

const DAY_KEYS = [
  'buildings.hours.sunday',
  'buildings.hours.monday',
  'buildings.hours.tuesday',
  'buildings.hours.wednesday',
  'buildings.hours.thursday',
  'buildings.hours.friday',
  'buildings.hours.saturday',
]

const SOCIAL_PLATFORMS = [
  { key: 'youtube', Icon: YoutubeLogoIcon, labelKey: 'dashboard.social.youtube' },
  { key: 'instagram', Icon: InstagramLogoIcon, labelKey: 'dashboard.social.instagram' },
  { key: 'linkedin', Icon: LinkedinLogoIcon, labelKey: 'dashboard.social.linkedin' },
  { key: 'facebook', Icon: FacebookLogoIcon, labelKey: 'dashboard.social.facebook' },
] as const

const CATEGORY_KEYS: Record<AmenityCategory, string> = {
  connectivity: 'buildings.amenities.connectivity',
  food: 'buildings.amenities.food',
  transport: 'buildings.amenities.transport',
  accessibility: 'buildings.amenities.accessibility',
  facilities: 'buildings.amenities.facilities',
}

function Section({
  title,
  summary,
  children,
}: {
  title: string
  summary?: string
  children: ReactNode
}) {
  return (
    <Collapsible.Root className="home-about__item">
      <Collapsible.Trigger className="home-about__summary">
        <span className="home-about__label">{title}</span>
        {summary && <span className="home-about__hint">{summary}</span>}
        <CaretDownIcon size={16} className="home-about__caret" aria-hidden />
      </Collapsible.Trigger>
      {/* hidden="until-found" like the old <details>: collapsed content stays in
          the DOM and find-in-page still opens the section. */}
      <Collapsible.Panel hiddenUntilFound className="home-about__body">
        {children}
      </Collapsible.Panel>
    </Collapsible.Root>
  )
}

export interface AboutBuildingProps {
  building: Building
  timeZone: string
  now: Date
}

/** Building info as Kumo `Collapsible` accordions, collapsed by default. */
export function AboutBuilding({ building, timeZone, now }: AboutBuildingProps) {
  const { t } = useTranslation()

  const sortedHours = [...building.hours].sort((a, b) => {
    // Monday first: 1,2,3,4,5,6,0
    const orderA = a.dayOfWeek === 0 ? 7 : a.dayOfWeek
    const orderB = b.dayOfWeek === 0 ? 7 : b.dayOfWeek
    return orderA - orderB
  })
  const today = hoursForToday(building.hours, timeZone, now)
  const hoursSummary = today
    ? today.isClosed
      ? t('dashboard.about.closedToday')
      : t('dashboard.about.todayHours', { open: today.openTime, close: today.closeTime })
    : undefined

  const amenitiesByCategory = new Map<string, BuildingAmenity[]>()
  for (const amenity of building.amenities) {
    const list = amenitiesByCategory.get(amenity.category) ?? []
    list.push(amenity)
    amenitiesByCategory.set(amenity.category, list)
  }

  const socials = building.socialLinks ?? {}
  const visibleSocials = SOCIAL_PLATFORMS.map((platform) => ({
    ...platform,
    href: socials[platform.key],
  })).filter((platform): platform is (typeof SOCIAL_PLATFORMS)[number] & { href: string } =>
    Boolean(platform.href),
  )
  const communityGroups = building.communityGroups ?? []
  const communitySummary = [
    communityGroups.length > 0 ? t('dashboard.about.whatsapp') : null,
    ...visibleSocials.map((s) => t(s.labelKey)),
  ]
    .filter(Boolean)
    .join(', ')

  const photos = [
    ...(building.imageUrl ? [{ url: building.imageUrl, caption: '', sortOrder: -1 }] : []),
    ...[...building.photos].sort((a, b) => a.sortOrder - b.sortOrder),
  ]

  const addressSummary = [building.city, building.state].filter(Boolean).join(', ')
  const amenitiesSummary =
    building.amenities.length > 3
      ? `${building.amenities
          .slice(0, 3)
          .map((a) => a.name)
          .join(', ')}…`
      : building.amenities.map((a) => a.name).join(', ')

  return (
    <section aria-labelledby="home-about" className="flex flex-col gap-3">
      <Text as="h2" variant="heading3" id="home-about">
        {t('dashboard.about.title', { building: building.name })}
      </Text>
      <div className="home-about">
        {sortedHours.length > 0 && (
          <Section title={t('dashboard.about.hours')} summary={hoursSummary}>
            <dl className="flex flex-col gap-1">
              {sortedHours.map((hour) => (
                <div
                  key={hour.dayOfWeek}
                  className={cn(
                    'flex justify-between text-sm',
                    hour.isClosed && 'text-kumo-inactive',
                  )}
                >
                  <dt>{t(DAY_KEYS[hour.dayOfWeek] ?? '')}</dt>
                  <dd className="tabular-nums">
                    {hour.isClosed
                      ? t('buildings.hours.closed')
                      : `${hour.openTime} – ${hour.closeTime}`}
                  </dd>
                </div>
              ))}
            </dl>
          </Section>
        )}

        <Section title={t('dashboard.about.address')} summary={addressSummary || undefined}>
          {building.description && (
            <p className="mb-3 text-sm text-kumo-subtle">{building.description}</p>
          )}
          <p className="mb-3 flex items-start gap-2 text-sm">
            <MapPinIcon size={16} className="text-kumo-subtle shrink-0 mt-0.5" aria-hidden />
            <span>{building.address}</span>
          </p>
          {building.googleMapsUrl && (
            <Link
              href={building.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium"
            >
              <NavigationArrowIcon size={16} />
              {t('dashboard.openInMaps')}
            </Link>
          )}
        </Section>

        {(building.phone || building.email) && (
          <Section
            title={t('dashboard.contact')}
            summary={
              [
                building.phone ? t('dashboard.about.phone') : null,
                building.email ? t('dashboard.about.email') : null,
              ]
                .filter(Boolean)
                .join(', ') || undefined
            }
          >
            <div className="flex flex-col gap-2">
              {building.phone && (
                <Link href={`tel:${building.phone}`} className="flex items-center gap-2 text-sm">
                  <PhoneIcon size={16} className="text-kumo-subtle" />
                  {building.phone}
                </Link>
              )}
              {building.email && (
                <Link href={`mailto:${building.email}`} className="flex items-center gap-2 text-sm">
                  <EnvelopeSimpleIcon size={16} className="text-kumo-subtle" />
                  {building.email}
                </Link>
              )}
            </div>
          </Section>
        )}

        {(communityGroups.length > 0 || visibleSocials.length > 0) && (
          <Section title={t('dashboard.about.community')} summary={communitySummary || undefined}>
            {communityGroups.length > 0 && (
              <div className="mb-3">
                <p className="mb-2 text-sm text-kumo-subtle">{t('dashboard.community.intro')}</p>
                <div className="flex flex-col gap-2">
                  {communityGroups.map((group, index) => (
                    <Link
                      key={index}
                      href={group.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm"
                    >
                      <WhatsappLogoIcon size={16} className="text-kumo-subtle" />
                      {group.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {visibleSocials.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {visibleSocials.map(({ key, href, Icon, labelKey }) => (
                  <Link
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm"
                  >
                    <Icon size={16} className="text-kumo-subtle" />
                    {t(labelKey)}
                  </Link>
                ))}
              </div>
            )}
          </Section>
        )}

        {amenitiesByCategory.size > 0 && (
          <Section title={t('buildings.amenities')} summary={amenitiesSummary || undefined}>
            {[...amenitiesByCategory.entries()].map(([category, items]) => (
              <div key={category} className="mb-3">
                <span className="mb-1 block text-xs font-medium text-kumo-subtle">
                  {t(CATEGORY_KEYS[category as AmenityCategory] ?? category)}
                </span>
                <div className="flex flex-wrap gap-2">
                  {items.map((amenity) => (
                    <Badge key={amenity.name} variant="secondary">
                      {amenity.name}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </Section>
        )}

        {photos.length > 0 && (
          <Section
            title={t('dashboard.about.photos')}
            summary={t('dashboard.about.photoCount', { count: photos.length })}
          >
            <ul className="home-photos">
              {photos.map((photo, index) => (
                <li key={index} className="home-photo">
                  <img
                    src={photo.url}
                    alt={photo.caption || building.name}
                    loading="lazy"
                    className="home-photo__img"
                  />
                  {photo.caption && <span className="home-photo__caption">{photo.caption}</span>}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </section>
  )
}
